import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { toast as sonnerToast } from 'sonner';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { clearSettingsCache } from '../hooks/useSettings';
import { PerfexSyncModal } from '../components/PerfexSyncModal';
import { CompanyProfile } from '../components/company/CompanyProfile';
import { CompanyUsers } from '../components/company/CompanyUsers';
import { SurveyTemplateManager } from '../components/SurveyTemplateManager';
import { MetaTemplatesManager } from '../components/MetaTemplatesManager';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

interface Settings {
  id: string;
  webshareApiKey?: string;
  webshareRotateDays?: number | null;
  webshareRotateOnCampaign?: boolean;
  webshareRotateMessages?: number | null;
  openaiApiKey?: string;
  groqApiKey?: string;
  chatwootUrl?: string;
  chatwootAccountId?: string;
  chatwootApiToken?: string;
  perfexUrl?: string;
  perfexToken?: string;
  sgaUrl?: string;
  sgaToken?: string;
  sgaActive?: boolean;
}

const settingsSchema = z.object({
  webshareApiKey: z.string().optional().refine((val) => !val || val.length >= 10, {
    message: 'API Key da Webshare deve ter pelo menos 10 caracteres'
  }),
  webshareRotateDays: z.union([z.number(), z.string(), z.null()]).optional(),
  webshareRotateOnCampaign: z.boolean().optional(),
  webshareRotateMessages: z.union([z.number(), z.string(), z.null()]).optional(),
  openaiApiKey: z.string().optional(),
  groqApiKey: z.string().optional(),
  chatwootUrl: z.string().optional(),
  chatwootAccountId: z.string().optional(),
  chatwootApiToken: z.string().optional(),
  perfexUrl: z.string().optional(),
  perfexToken: z.string().optional(),
  sgaUrl: z.string().optional(),
  sgaToken: z.string().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

type TabType = 'company' | 'ai' | 'integrations' | 'surveys' | 'metaTemplates';

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<'openai' | 'groq' | 'chatwoot' | 'perfex' | 'sga' | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('company');
  const [activeSubTab, setActiveSubTab] = useState<'profile' | 'users'>('profile');
  const [activeIntegrationSubTab, setActiveIntegrationSubTab] = useState<'webshare' | 'evocrm' | 'bchat' | 'perfex' | 'sga'>('webshare');
  const [testCpf, setTestCpf] = useState('');
  const [isTestingSga, setIsTestingSga] = useState(false);
  const [sgaTestedSuccessfully, setSgaTestedSuccessfully] = useState(false);
  const isInitialLoad = useRef(true);
  const { user } = useAuth();

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const tenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // CRITICAL FIX: Always send X-Tenant-Id header for proper tenant isolation
    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };


  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
  });

  // Reset sgaTestedSuccessfully when sgaUrl or sgaToken changes
  const watchedSgaUrl = watch('sgaUrl');
  const watchedSgaToken = watch('sgaToken');

  useEffect(() => {
    // Skip on initial load to avoid clearing if data came from backend
    if (isInitialLoad.current && settings?.sgaUrl) {
      isInitialLoad.current = false;
      return;
    }
    
    // Only reset if the values are actually different from what's in settings (if settings loaded)
    if (settings && (watchedSgaUrl !== settings.sgaUrl || (watchedSgaToken && watchedSgaToken !== '****'))) {
       setSgaTestedSuccessfully(false);
    }
  }, [watchedSgaUrl, watchedSgaToken, settings]);

  const loadSettings = useCallback(async () => {
    try {
      let url = '/api/settings';

      // Use single source of truth for tenant ID
      const selectedTenantId = localStorage.getItem('selected_tenant_id');
      console.log('🔍 SettingsPage.loadSettings - selectedTenantId from localStorage:', selectedTenantId);

      if (selectedTenantId) {
        url = `/api/settings?tenantId=${selectedTenantId}`;
      }

      console.log('🔍 SettingsPage.loadSettings - fetching URL:', url);

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ SettingsPage.loadSettings - API response sgaUrl:', data.sgaUrl, 'sgaToken:', data.sgaToken ? '****' : null);
        setSettings(data);
        setValue('webshareApiKey', data.webshareApiKey || '');
        setValue('webshareRotateDays', data.webshareRotateDays !== null && data.webshareRotateDays !== undefined ? data.webshareRotateDays : '');
        setValue('webshareRotateOnCampaign', !!data.webshareRotateOnCampaign);
        setValue('webshareRotateMessages', data.webshareRotateMessages !== null && data.webshareRotateMessages !== undefined ? data.webshareRotateMessages : '');
        setValue('openaiApiKey', data.openaiApiKey || '');
        setValue('groqApiKey', data.groqApiKey || '');
        setValue('chatwootUrl', data.chatwootUrl || '');
        setValue('chatwootAccountId', data.chatwootAccountId || '');
        setValue('chatwootApiToken', data.chatwootApiToken || '');
        setValue('perfexUrl', data.perfexUrl || '');
        setValue('perfexToken', data.perfexToken || '');
        setValue('sgaUrl', data.sgaUrl || '');
        setValue('sgaToken', data.sgaToken || '');

        // Se já tem configuração ativa salva, marca como testado
        if (data.sgaActive) {
          setSgaTestedSuccessfully(true);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [setValue]);

  useEffect(() => {
    loadSettings();

    // Listen for tenant changes from header selector
    const handleTenantChange = () => {
      loadSettings();
    };

    window.addEventListener('superadmin-tenant-changed', handleTenantChange);
    return () => {
      window.removeEventListener('superadmin-tenant-changed', handleTenantChange);
    };
  }, [loadSettings]);

  const onSubmit = async (data: SettingsFormData) => {
    console.log('🚀 INICIANDO SUBMIT DO FORMULÁRIO', data);
    const currentModal = activeModal;
    console.log('🚀 SettingsPage.onSubmit - currentModal:', currentModal);
    console.log('🚀 SettingsPage.onSubmit - data:', { ...data, sgaToken: data.sgaToken ? '****' : null });
    
    // Feedback visual imediato
    const toastId = sonnerToast.loading('Salvando configurações...', {
      style: { zIndex: 10000 }
    });

    try {
      // Always include tenantId from single source of truth
      const selectedTenantId = localStorage.getItem('selected_tenant_id');
      console.log('🚀 SettingsPage.onSubmit - selectedTenantId:', selectedTenantId);
      
      const requestData: SettingsFormData & { tenantId?: string; sgaActive?: boolean } = selectedTenantId
        ? { ...data, tenantId: selectedTenantId }
        : data;

      // Include sgaActive status based on current test state
      if (currentModal === 'sga') {
        requestData.sgaActive = sgaTestedSuccessfully;
      }

      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });

      console.log('🚀 SettingsPage.onSubmit - response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('🚀 SettingsPage.onSubmit - result:', result);
        
        sonnerToast.dismiss(toastId);

        if (currentModal === 'sga') {
          console.log('🔔 Chamando sonnerToast para SGA');
          sonnerToast.success('Configurações do SGA salvas com sucesso!', {
            style: {
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              zIndex: 10000
            },
            duration: 5000,
          });
        } else {
          sonnerToast.success('Configurações salvas com sucesso!', {
            style: { zIndex: 10000 }
          });
        }
        
        setActiveModal(null);
        clearSettingsCache();
        // Force a small delay to ensure DB persistence before reload
        setTimeout(() => {
          loadSettings();
        }, 500);
      } else {
        const errorData = await response.json();
        sonnerToast.dismiss(toastId);
        sonnerToast.error(errorData.error || 'Erro ao salvar configurações', {
          style: { zIndex: 10000 }
        });
      }
    } catch (error) {
      console.error('Erro ao salvar configurações de integração:', error);
      sonnerToast.dismiss(toastId);
      sonnerToast.error('Erro ao salvar configurações de integração', {
        style: { zIndex: 10000 }
      });
    }
  };

  const removeIntegration = async (type: 'openai' | 'groq' | 'chatwoot' | 'perfex' | 'sga') => {
    const integrationNames = {
      openai: 'OpenAI',
      groq: 'Groq',
      chatwoot: 'BChat',
      perfex: 'Perfex CRM',
      sga: 'SGA'
    };

    showConfirm(
      'Remover Integração',
      `Tem certeza que deseja remover a integração com ${integrationNames[type]}?`,
      async () => {
        try {
          const requestData: Record<string, unknown> = {};

          if (type === 'openai') {
            requestData.openaiApiKey = '';
          } else if (type === 'groq') {
            requestData.groqApiKey = '';
          } else if (type === 'chatwoot') {
            requestData.chatwootUrl = '';
            requestData.chatwootAccountId = '';
            requestData.chatwootApiToken = '';
          } else if (type === 'perfex') {
            requestData.perfexUrl = '';
            requestData.perfexToken = '';
          } else if (type === 'sga') {
            requestData.sgaUrl = '';
            requestData.sgaToken = '';
          }

          if (user?.role === 'SUPERADMIN') {
            const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
            if (selectedTenantId) {
              requestData.tenantId = selectedTenantId;
            }
          }

          const response = await authenticatedFetch('/api/settings', {
            method: 'PUT',
            body: JSON.stringify(requestData),
          });

          if (response.ok) {
            toast.success(`Integração com ${integrationNames[type]} removida com sucesso`);
            setActiveModal(null);
            clearSettingsCache();
            await loadSettings();
          } else {
            const errorData = await response.json();
            toast.error(errorData.error || 'Erro ao remover integração');
          }
        } catch (error) {
          console.error('Erro ao remover integração:', error);
          toast.error('Erro ao remover integração');
        }
      },
      'danger'
    );
  };


  const handleTestSga = async () => {
    if (!testCpf) {
      toast.error('Por favor, informe um CPF para teste');
      return;
    }

    setIsTestingSga(true);
    try {
      const sgaUrl = getValues('sgaUrl');
      const sgaToken = getValues('sgaToken');

      if (!sgaUrl || !sgaToken) {
        toast.error('Informe a URL e o Token antes de testar');
        return;
      }

      const response = await authenticatedFetch('/api/settings/sga/test', {
        method: 'POST',
        body: JSON.stringify({
          sgaUrl,
          sgaToken,
          cpf: testCpf
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('SGA Test Result:', data);
        setSgaTestedSuccessfully(true);
        toast.success('Conexão com SGA realizada com sucesso! Verifique o console para detalhes.');
      } else {
        const errorData = await response.json();
        console.error('SGA Test Error:', errorData);
        toast.error(`Erro no teste: ${errorData.details?.message || errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao testar SGA:', error);
      toast.error('Erro ao realizar teste de conexão');
    } finally {
      setIsTestingSga(false);
    }
  };

  const handleTestWebshare = async () => {
    const webshareApiKey = (getValues('webshareApiKey') || '').trim();
    if (!webshareApiKey) {
      toast.error('Informe a API Key da Webshare para testar');
      return;
    }

    try {
      const response = await authenticatedFetch('/api/settings/test-connection', {
        method: 'POST',
        body: JSON.stringify({
          service: 'webshare',
          config: { apiKey: webshareApiKey }
        })
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(data.message || 'Conexão com Webshare realizada com sucesso');
      } else {
        toast.error(data.message || data.error || 'Falha ao validar API Key da Webshare');
      }
    } catch (error) {
      console.error('Erro ao testar conexão Webshare:', error);
      toast.error('Erro ao testar conexão com Webshare');
    }
  };

  const handleSaveWebshare = async () => {
    const webshareApiKey = (getValues('webshareApiKey') || '').trim();
    
    // Pegar valores brutos para evitar null se for string vazia
    const rawDays = getValues('webshareRotateDays');
    const rawCampaign = getValues('webshareRotateOnCampaign');
    const rawMessages = getValues('webshareRotateMessages');

    const webshareRotateDays = rawDays !== '' && rawDays !== null && rawDays !== undefined && !isNaN(Number(rawDays)) ? Number(rawDays) : null;
    const webshareRotateOnCampaign = !!rawCampaign;
    const webshareRotateMessages = rawMessages !== '' && rawMessages !== null && rawMessages !== undefined && !isNaN(Number(rawMessages)) ? Number(rawMessages) : null;

    if (webshareApiKey && webshareApiKey.length < 10) {
      toast.error('API Key da Webshare deve ter pelo menos 10 caracteres');
      return;
    }

    const selectedTenantId = localStorage.getItem('selected_tenant_id');
    const requestData: any = { 
      webshareApiKey,
      webshareRotateDays,
      webshareRotateOnCampaign,
      webshareRotateMessages
    };
    
    if (selectedTenantId) {
      requestData.tenantId = selectedTenantId;
    }

    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData)
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success('API Key da Webshare salva com sucesso');
        clearSettingsCache();
        await loadSettings();
      } else {
        toast.error(data.error || data.message || 'Erro ao salvar API Key da Webshare');
      }
    } catch (error) {
      console.error('Erro ao salvar Webshare:', error);
      toast.error('Erro ao salvar API Key da Webshare');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-black">
        <Header title="Configurações" />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <span className="text-gray-400 font-oswald uppercase tracking-widest font-bold">Carregando configurações...</span>
        </div>
      </div>
    );
  }
  const tabs = [
    {
      id: 'company' as TabType,
      label: 'Minha Empresa',
      icon: '🏢',
      description: 'Gerencie perfil e usuários'
    },
    {
      id: 'ai' as TabType,
      label: 'Inteligência Artificial',
      icon: '🤖',
      description: 'Configure OpenAI e Groq'
    },
    {
      id: 'integrations' as TabType,
      label: 'Integrações',
      icon: '🔗',
      description: 'Webshare, BChat, Perfex, SGA'
    },
    {
      id: 'surveys' as TabType,
      label: 'Pesquisas (CSAT/NPS)',
      icon: '📊',
      description: 'Gerencie seus templates de pesquisa'
    },
    {
      id: 'metaTemplates' as TabType,
      label: 'Templates Meta',
      icon: '📝',
      description: 'Gerencie templates do Meta WhatsApp'
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-[#1e3a5f] to-black">
      <Header
        title="Configurações"
        subtitle="Configure as definições do sistema"
        compact
        className="rounded-b-[2rem]"
      />

      <div className="flex flex-1 overflow-hidden p-4 pt-3 gap-3">
        {/* Sidebar Navigation */}
        <div className="lg:w-80 flex flex-col">
          <div className="bg-white/5 backdrop-blur-md rounded-[2rem] border border-white/10 p-6 shadow-xl">
            <h3 className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] mb-6 px-2">
              Menus de Configuração
            </h3>
            <div className="flex flex-col gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all duration-300 group relative overflow-hidden ${activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)]'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                >
                  <span className="relative z-10 text-xl">{tab.icon}</span>
                  <div className="relative z-10 flex flex-col items-start">
                    <span className="font-bold text-xs uppercase tracking-wider">{tab.label}</span>
                    <span className={`text-[9px] uppercase tracking-tight opacity-60 ${activeTab === tab.id ? 'text-blue-100' : 'text-gray-500'}`}>
                      {tab.description}
                    </span>
                  </div>
                  {activeTab === tab.id && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent animate-pulse" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-full mx-auto space-y-4 pb-20">

            {/* Company Tab */}
            {activeTab === 'company' && (
              <div className="animate-fadeIn">
                {/* Sub-tabs Navigation */}
                <div className="flex bg-white/5 backdrop-blur-md p-1.5 rounded-2xl border border-white/10 mb-6 w-fit shadow-lg">
                  <button
                    onClick={() => setActiveSubTab('profile')}
                    className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 relative overflow-hidden group/btn ${activeSubTab === 'profile'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                    Perfil da Empresa
                  </button>
                  <button
                    onClick={() => setActiveSubTab('users')}
                    className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 relative overflow-hidden group/btn ${activeSubTab === 'users'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                    Usuários
                  </button>
                </div>

                {activeSubTab === 'profile' ? <CompanyProfile /> : <CompanyUsers />}
              </div>
            )}

            {/* AI Integrations Tab */}
            {activeTab === 'ai' && (
              <div className="animate-fadeIn">
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
                  
                  <div className="mb-10 relative z-10">
                    <h2 className="text-3xl font-bold font-oswald text-white flex items-center gap-4 uppercase tracking-tight">
                      <span className="p-3 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-2xl border border-blue-500/30 shadow-lg">🤖</span>
                      Inteligência Artificial
                    </h2>
                    <p className="text-gray-400 mt-3 text-lg max-w-2xl">Potencialize suas campanhas com os modelos de linguagem mais avançados do mercado.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                    {/* OpenAI Card */}
                    <div className="group relative bg-white/5 rounded-2xl border border-white/10 p-8 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-500 overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-green-500/10 transition-colors"></div>
                      
                      <div className="flex items-start justify-between mb-8 relative z-10">
                        <div className="flex items-center gap-5">
                          <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center text-3xl border border-green-500/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                            🤖
                          </div>
                          <div>
                            <h3 className="font-bold font-oswald text-2xl text-white tracking-tight">OpenAI</h3>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] mt-1">GPT-4, GPT-3.5</p>
                          </div>
                        </div>
                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase shadow-sm ${settings?.openaiApiKey
                          ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/40'
                          : 'bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/40'
                          }`}>
                          {settings?.openaiApiKey ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      <p className="text-gray-400 mb-8 text-sm leading-relaxed relative z-10 font-medium">
                        Integre com os modelos mais avançados da OpenAI para criar mensagens personalizadas, inteligentes e altamente conversacionais.
                      </p>

                      <button
                        onClick={() => setActiveModal('openai')}
                        className="w-full py-4 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm bg-white text-black hover:bg-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative z-10 overflow-hidden group/btn"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/5 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                        {settings?.openaiApiKey ? 'Gerenciar Chave' : 'Configurar Integração'}
                      </button>
                    </div>

                    {/* Groq Card */}
                    <div className="group relative bg-white/5 rounded-2xl border border-white/10 p-8 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-500 overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-orange-500/10 transition-colors"></div>
                      
                      <div className="flex items-start justify-between mb-8 relative z-10">
                        <div className="flex items-center gap-5">
                          <div className="w-16 h-16 bg-orange-500/10 rounded-2xl flex items-center justify-center text-3xl border border-orange-500/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                            ⚡
                          </div>
                          <div>
                            <h3 className="font-bold font-oswald text-2xl text-white tracking-tight">Groq Cloud</h3>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] mt-1">Llama 3, Mixtral</p>
                          </div>
                        </div>
                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase shadow-sm ${settings?.groqApiKey
                          ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/40'
                          : 'bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/40'
                          }`}>
                          {settings?.groqApiKey ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      <p className="text-gray-400 mb-8 text-sm leading-relaxed relative z-10 font-medium">
                        Velocidade extrema com modelos open-source de última geração via Groq LPU. Ideal para processamento em massa e respostas instantâneas.
                      </p>

                      <button
                        onClick={() => setActiveModal('groq')}
                        className="w-full py-4 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm bg-orange-600 text-white hover:bg-orange-500 hover:shadow-lg hover:shadow-orange-900/30 hover:-translate-y-1 transition-all duration-300 relative z-10 overflow-hidden group/btn"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                        {settings?.groqApiKey ? 'Gerenciar Chave' : 'Configurar Integração'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="animate-fadeIn">
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-600/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

                  <div className="flex bg-white/5 backdrop-blur-md p-1.5 rounded-2xl border border-white/10 mb-6 w-fit shadow-lg relative z-10">
                    <button
                      onClick={() => setActiveIntegrationSubTab('webshare')}
                      className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 ${activeIntegrationSubTab === 'webshare'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                      Webshare
                    </button>
                    <button
                      onClick={() => setActiveIntegrationSubTab('evocrm')}
                      className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 ${activeIntegrationSubTab === 'evocrm'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                      EvoCRM
                    </button>
                    <button
                      onClick={() => setActiveIntegrationSubTab('bchat')}
                      className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 ${activeIntegrationSubTab === 'bchat'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                      BChat
                    </button>
                    <button
                      onClick={() => setActiveIntegrationSubTab('perfex')}
                      className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 ${activeIntegrationSubTab === 'perfex'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                      Perfex
                    </button>
                    <button
                      onClick={() => setActiveIntegrationSubTab('sga')}
                      className={`px-8 py-2.5 rounded-xl text-sm font-bold font-oswald uppercase tracking-[0.2em] transition-all duration-300 ${activeIntegrationSubTab === 'sga'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-400/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                      SGA
                    </button>
                  </div>

                  {activeIntegrationSubTab === 'webshare' && (
                    <div className="bg-white/5 rounded-2xl border border-white/10 p-8 relative z-10">
                      <h3 className="font-bold font-oswald text-2xl text-white tracking-tight mb-2">Webshare</h3>
                      <p className="text-sm text-gray-400 mb-6">
                        Defina a API Key para habilitar proxy rotativo automático nas instâncias EVOGO.
                      </p>

                      <div className="group mb-6">
                        <label htmlFor="webshareApiKey" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] ml-1">
                          API Key
                        </label>
                        <input
                          id="webshareApiKey"
                          type="password"
                          {...register('webshareApiKey')}
                          placeholder="Token da Webshare"
                          className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all text-sm text-white placeholder-gray-600"
                        />
                        {errors.webshareApiKey && (
                          <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block">
                            {errors.webshareApiKey.message}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="space-y-2">
                          <label className="text-xs font-bold font-oswald uppercase tracking-widest text-gray-400 ml-1 flex items-center gap-2">
                            <span>Rotacionar a cada (em dias)</span>
                          </label>
                          <input
                            {...register('webshareRotateDays')}
                            type="number"
                            min="1"
                            placeholder="Ex: 7"
                            className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all text-sm text-white placeholder-gray-600"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-bold font-oswald uppercase tracking-widest text-gray-400 ml-1 flex items-center gap-2">
                            <span>Número de mensagens enviadas</span>
                          </label>
                          <input
                            {...register('webshareRotateMessages')}
                            type="number"
                            min="1"
                            placeholder="Ex: 500"
                            className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all text-sm text-white placeholder-gray-600"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-6 p-4 bg-white/5 border border-white/10 rounded-2xl">
                        <input
                          {...register('webshareRotateOnCampaign')}
                          type="checkbox"
                          id="webshareRotateOnCampaign"
                          className="w-5 h-5 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-900 bg-white/5 cursor-pointer"
                        />
                        <label htmlFor="webshareRotateOnCampaign" className="text-sm font-bold font-oswald uppercase tracking-widest text-gray-300 cursor-pointer">
                          Rotacionar a cada nova campanha
                        </label>
                      </div>

                      <div className="flex flex-wrap gap-4 mt-6">
                        <button
                          type="button"
                          onClick={handleTestWebshare}
                          className="px-8 py-3 rounded-xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] bg-white/10 text-white hover:bg-white/20 transition-all border border-white/10"
                        >
                          Testar conexão
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveWebshare}
                          className="px-8 py-3 rounded-xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] bg-cyan-600 text-white hover:bg-cyan-500 transition-all border border-cyan-500"
                        >
                          Salvar API Key
                        </button>
                      </div>
                    </div>
                  )}

                  {activeIntegrationSubTab === 'evocrm' && (
                    <div className="bg-white/5 rounded-2xl border border-white/10 p-8 relative z-10">
                      <h3 className="font-bold font-oswald text-2xl text-white tracking-tight mb-2">EvoCRM</h3>
                      <p className="text-sm text-gray-400">
                        Subaba pronta para receber os campos do EvoCRM conforme próxima etapa.
                      </p>
                    </div>
                  )}

                  {activeIntegrationSubTab === 'bchat' && (
                    <div className="animate-fadeIn">
                      <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-10 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

                        <div className="bg-white/5 rounded-2xl border border-white/10 p-12 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-500 relative z-10 overflow-hidden group">
                          <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600/5 rounded-full blur-2xl -mr-20 -mt-20 group-hover:bg-blue-600/10 transition-colors"></div>

                          <div className="flex flex-col md:flex-row items-center justify-between gap-10 relative z-10">
                            <div className="flex items-center gap-8">
                              <div className="w-24 h-24 bg-blue-500/10 rounded-3xl flex items-center justify-center text-5xl border border-blue-500/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                                💬
                              </div>
                              <div>
                                <h3 className="font-bold font-oswald text-2xl text-white mb-2 tracking-tight">BChat Platform</h3>
                                <div className="flex items-center gap-3">
                                  <div className={`h-3 w-3 rounded-full ${settings?.chatwootUrl ? 'bg-green-500 animate-pulse shadow-[0_0_12px_rgba(34,197,94,0.8)]' : 'bg-gray-600'}`} />
                                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-300">
                                    {settings?.chatwootUrl ? 'Sistema Conectado' : 'Não vinculado'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-col items-stretch md:items-end gap-4 min-w-[240px]">
                              <span className={`px-5 py-2 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase text-center shadow-sm ${settings?.chatwootUrl
                                ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/40'
                                : 'bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/40'
                                }`}>
                                {settings?.chatwootUrl ? 'Status: Operacional' : 'Status: Inativo'}
                              </span>
                              <button
                                onClick={() => setActiveModal('chatwoot')}
                                className="w-full py-4 px-8 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm bg-blue-600 text-white hover:bg-blue-500 hover:shadow-xl hover:shadow-blue-900/30 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group/btn"
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                {settings?.chatwootUrl ? 'Editar Configuração' : 'Conectar BChat'}
                              </button>
                            </div>
                          </div>

                          {settings?.chatwootUrl && (
                            <div className="mt-10 p-6 bg-blue-500/10 rounded-2xl border border-blue-500/20 flex items-start gap-4 relative z-10">
                              <span className="text-blue-400 text-xl">ⓘ</span>
                              <div className="text-sm">
                                <p className="font-bold mb-2 text-blue-100 font-oswald uppercase tracking-[0.2em]">Instância Ativa</p>
                                <div className="space-y-1 text-blue-200/80 font-medium">
                                  <p className="break-all">{settings.chatwootUrl}</p>
                                  <p className="flex items-center gap-2">
                                    <span className="text-blue-400/60 uppercase text-[10px] font-bold">Account ID:</span>
                                    {settings.chatwootAccountId}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeIntegrationSubTab === 'perfex' && (
                    <div className="animate-fadeIn">
                      <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

                        <div className="bg-white/5 rounded-2xl border border-white/10 p-10 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-500 relative z-10 overflow-hidden group">
                          <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/5 rounded-full blur-2xl -mr-20 -mt-20 group-hover:bg-purple-600/10 transition-colors"></div>

                          <div className="flex flex-col md:flex-row items-center justify-between gap-10 relative z-10">
                            <div className="flex items-center gap-8">
                              <div className="w-24 h-24 bg-purple-500/10 rounded-3xl flex items-center justify-center text-5xl border border-purple-500/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                                🔧
                              </div>
                              <div>
                                <h3 className="font-bold font-oswald text-2xl text-white mb-2 tracking-tight">Perfex CRM</h3>
                                <p className="text-gray-400 mb-5 max-w-md text-sm leading-relaxed">Automatize a importação de contatos e faça o gerenciamento unificado de seus clientes.</p>
                                <div className="flex items-center gap-3">
                                  <div className={`h-3 w-3 rounded-full ${settings?.perfexUrl ? 'bg-green-500 animate-pulse shadow-[0_0_12px_rgba(34,197,94,0.8)]' : 'bg-gray-600'}`} />
                                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-300">
                                    {settings?.perfexUrl ? 'Sincronização Ativa' : 'Não vinculado'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-col items-stretch md:items-end gap-4 min-w-[240px]">
                              <span className={`px-5 py-2 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase text-center shadow-sm ${settings?.perfexUrl
                                ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/40'
                                : 'bg-gray-500/20 text-gray-400 ring-1 ring-gray-500/40'
                                }`}>
                                {settings?.perfexUrl ? 'Status: Ativo' : 'Status: Inativo'}
                              </span>
                              <button
                                onClick={() => setActiveModal('perfex')}
                                className="w-full py-4 px-8 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm bg-purple-600 text-white hover:bg-purple-500 hover:shadow-xl hover:shadow-purple-900/30 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group/btn"
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                {settings?.perfexUrl ? 'Editar Configuração' : 'Conectar CRM'}
                              </button>
                            </div>
                          </div>

                          {settings?.perfexUrl && (
                            <div className="mt-10 p-6 bg-purple-500/10 rounded-2xl border border-purple-500/20 flex items-start gap-4 relative z-10">
                              <span className="text-purple-400 text-xl">ⓘ</span>
                              <div className="text-sm">
                                <p className="font-bold mb-2 text-purple-100 font-oswald uppercase tracking-[0.2em]">CRM Conectado</p>
                                <div className="space-y-1 text-purple-200/80 font-medium">
                                  <p className="break-all">{settings.perfexUrl}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeIntegrationSubTab === 'sga' && (
                    <div className="animate-fadeIn">
                      <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-600/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

                        <div className="bg-white/5 rounded-2xl border border-white/10 p-10 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-500 relative z-10 overflow-hidden group">
                          <div className="absolute top-0 right-0 w-40 h-40 bg-teal-600/5 rounded-full blur-2xl -mr-20 -mt-20 group-hover:bg-teal-600/10 transition-colors"></div>

                          <div className="flex flex-col md:flex-row items-center justify-between gap-10 relative z-10">
                            <div className="flex items-center gap-8">
                              <div className="w-24 h-24 bg-teal-500/10 rounded-3xl flex items-center justify-center text-5xl border border-teal-500/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                                🔗
                              </div>
                              <div>
                                <h3 className="font-bold font-oswald text-2xl text-white mb-2 tracking-tight">SGA System</h3>
                                <p className="text-gray-400 mb-5 max-w-md text-sm leading-relaxed">Gerencie a conexão com o sistema legado SGA para consulta de dados em tempo real.</p>
                                <div className="flex items-center gap-3">
                                  <div className={`h-3 w-3 rounded-full ${settings?.sgaActive ? 'bg-green-500 animate-pulse shadow-[0_0_12px_rgba(34,197,94,0.8)]' : 'bg-yellow-500'}`} />
                                  <span className={`text-xs font-bold uppercase tracking-[0.2em] ${settings?.sgaActive ? 'text-gray-300' : 'text-yellow-500'}`}>
                                    {settings?.sgaActive ? 'Sistema Conectado' : 'Aguardando Verificação'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-col items-stretch md:items-end gap-4 min-w-[240px]">
                              <span className={`px-5 py-2 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase text-center shadow-sm ${settings?.sgaActive
                                ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/40'
                                : 'bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/40'
                                }`}>
                                {settings?.sgaActive ? 'Status: Ativo' : 'Status: Não Verificado'}
                              </span>
                              <button
                                onClick={() => setActiveModal('sga')}
                                className="w-full py-4 px-8 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm bg-teal-600 text-white hover:bg-teal-500 hover:shadow-xl hover:shadow-teal-900/30 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group/btn"
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                                {settings?.sgaUrl ? 'Editar Configuração' : 'Conectar SGA'}
                              </button>
                            </div>
                          </div>

                          {settings?.sgaUrl && settings?.sgaToken && (
                            <div className="mt-10 p-6 bg-teal-500/10 rounded-2xl border border-teal-500/20 flex items-start gap-4 relative z-10">
                              <span className="text-teal-400 text-xl">ⓘ</span>
                              <div className="text-sm">
                                <p className="font-bold mb-2 text-teal-100 font-oswald uppercase tracking-[0.2em]">SGA Conectado</p>
                                <div className="space-y-1 text-teal-200/80 font-medium">
                                  <p className="break-all">{settings.sgaUrl}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BChat Integration Tab */}
            {activeTab === 'surveys' && (
              <div className="animate-fadeIn">
                <SurveyTemplateManager />
              </div>
            )}
            {activeTab === 'metaTemplates' && (
              <div className="animate-fadeIn">
                <MetaTemplatesManager />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modais de Integração - Premium Style */}

      {/* Modal OpenAI */}
      {activeModal === 'openai' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-white/10 relative z-10 animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl -mr-16 -mt-16" />
            
            <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600/10 to-transparent" />
              <h3 className="relative text-2xl font-bold font-oswald text-white flex items-center gap-4 uppercase tracking-tight">
                <span className="p-2 bg-green-500/10 rounded-xl border border-green-500/20 text-xl shadow-[0_0_15px_rgba(34,197,94,0.3)]">🤖</span> 
                OpenAI Integration
              </h3>
              <button onClick={() => setActiveModal(null)} className="relative text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-full w-10 h-10 flex items-center justify-center border border-white/10 hover:rotate-90 duration-300">
                <span className="text-xl">✕</span>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="relative p-8 space-y-8">
              <div className="group">
                <label htmlFor="openaiApiKey" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-blue-400 transition-colors ml-1">
                  API Key OpenAI
                </label>
                <div className="relative">
                  <input
                    id="openaiApiKey"
                    type="password"
                    {...register('openaiApiKey')}
                    placeholder="sk-..."
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all font-mono text-sm text-white placeholder-gray-600"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500 group-focus-within:text-blue-500 transition-colors">
                    <span className="text-xs font-mono">key</span>
                  </div>
                </div>
                {errors.openaiApiKey && (
                  <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                    {errors.openaiApiKey.message}
                  </p>
                )}
                <p className="text-[10px] text-gray-500 mt-3 ml-1 font-bold uppercase tracking-[0.15em]">
                  Chave API para processamento de linguagem natural
                </p>
              </div>

              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-4 text-gray-400 bg-white/5 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-white/10 hover:text-white transition-all border border-white/10"
                >
                  Cancelar
                </button>
                {settings?.openaiApiKey && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('openai')}
                    disabled={isSubmitting}
                    className="px-6 py-4 text-red-400 bg-red-500/10 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-red-500/20 transition-all border border-red-500/20 disabled:opacity-50"
                  >
                    Remover
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:shadow-xl hover:-translate-y-1 active:scale-95 disabled:opacity-50 relative overflow-hidden group/btn"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      <span>Salvando...</span>
                    </div>
                  ) : (
                    'Salvar Chave'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Groq */}
      {activeModal === 'groq' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-white/10 relative z-10 animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl -mr-16 -mt-16" />
            
            <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 to-transparent" />
              <h3 className="relative text-2xl font-bold font-oswald text-white flex items-center gap-4 uppercase tracking-tight">
                <span className="p-2 bg-orange-500/10 rounded-xl border border-orange-500/20 text-xl shadow-[0_0_15px_rgba(234,88,12,0.3)]">⚡</span> 
                Groq Cloud
              </h3>
              <button onClick={() => setActiveModal(null)} className="relative text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-full w-10 h-10 flex items-center justify-center border border-white/10 hover:rotate-90 duration-300">
                <span className="text-xl">✕</span>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="relative p-8 space-y-8">
              <div className="group">
                <label htmlFor="groqApiKey" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-orange-400 transition-colors ml-1">
                  API Key Groq
                </label>
                <div className="relative">
                  <input
                    id="groqApiKey"
                    type="password"
                    {...register('groqApiKey')}
                    placeholder="gsk_..."
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-orange-600/50 focus:border-orange-600 transition-all font-mono text-sm text-white placeholder-gray-600"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500 group-focus-within:text-orange-500 transition-colors">
                    <span className="text-xs font-mono">LPU</span>
                  </div>
                </div>
                {errors.groqApiKey && (
                  <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                    {errors.groqApiKey.message}
                  </p>
                )}
                <p className="text-[10px] text-gray-500 mt-3 ml-1 font-bold uppercase tracking-[0.15em]">
                  Chave API para processamento ultra-rápido via LPU
                </p>
              </div>

              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-4 text-gray-400 bg-white/5 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-white/10 hover:text-white transition-all border border-white/10"
                >
                  Cancelar
                </button>
                {settings?.groqApiKey && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('groq')}
                    disabled={isSubmitting}
                    className="px-6 py-4 text-red-400 bg-red-500/10 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-red-500/20 transition-all border border-red-500/20 disabled:opacity-50"
                  >
                    Remover
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-4 bg-orange-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-orange-500 transition-all shadow-lg shadow-orange-900/20 hover:shadow-xl hover:-translate-y-1 active:scale-95 disabled:opacity-50 relative overflow-hidden group/btn"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      <span>Salvando...</span>
                    </div>
                  ) : (
                    'Salvar Chave'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal BChat */}
      {activeModal === 'chatwoot' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-white/10 relative z-10 animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 rounded-full blur-3xl -mr-16 -mt-16" />
            
            <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent" />
              <h3 className="relative text-2xl font-bold font-oswald text-white flex items-center gap-4 uppercase tracking-tight">
                <span className="p-2 bg-blue-500/10 rounded-xl border border-blue-500/20 text-xl shadow-[0_0_15px_rgba(37,99,235,0.3)]">💬</span> 
                INTEGRAÇÃO BCHAT
              </h3>
              <button onClick={() => setActiveModal(null)} className="relative text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-full w-10 h-10 flex items-center justify-center border border-white/10 hover:rotate-90 duration-300">
                <span className="text-xl">✕</span>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit, (errors) => {
              console.error('❌ Erros de validação no formulário:', errors);
              sonnerToast.error('Verifique os campos obrigatórios', { style: { zIndex: 10000 } });
            })} className="relative p-8 space-y-6">
              <div className="group">
                <label htmlFor="chatwootUrl" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-blue-400 transition-colors ml-1">
                  URL BChat *
                </label>
                <input
                  id="chatwootUrl"
                  type="url"
                  {...register('chatwootUrl')}
                  placeholder="https://app.bchat.com.br"
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all text-sm text-white placeholder-gray-600"
                />
                {errors.chatwootUrl && (
                  <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                    {errors.chatwootUrl.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="group">
                  <label htmlFor="chatwootAccountId" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-blue-400 transition-colors ml-1">
                    ID da Conta *
                  </label>
                  <input
                    id="chatwootAccountId"
                    type="text"
                    {...register('chatwootAccountId')}
                    placeholder="123456"
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all text-sm text-white placeholder-gray-600"
                  />
                  {errors.chatwootAccountId && (
                    <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                      {errors.chatwootAccountId.message}
                    </p>
                  )}
                </div>

                <div className="group">
                  <label htmlFor="chatwootApiToken" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-blue-400 transition-colors ml-1">
                    Token de API *
                  </label>
                  <input
                    id="chatwootApiToken"
                    type="password"
                    {...register('chatwootApiToken')}
                    placeholder="••••••••••••••••"
                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all font-mono text-sm text-white placeholder-gray-600"
                  />
                  {errors.chatwootApiToken && (
                    <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                      {errors.chatwootApiToken.message}
                    </p>
                  )}
                </div>
              </div>

              <p className="text-[10px] text-gray-500 mt-2 ml-1 font-bold uppercase tracking-[0.15em]">
                Disponível em: Configurações &gt; Perfil &gt; Access Token
              </p>

              <div className="flex gap-4 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-8 py-4 text-gray-400 bg-white/5 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-white/10 hover:text-white transition-all border border-white/10 hover:border-white/20"
                >
                  Cancelar
                </button>
                {settings?.chatwootUrl && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('chatwoot')}
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-4 text-red-400 bg-red-500/10 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-red-500/20 hover:text-red-300 transition-all border border-red-500/20 disabled:opacity-50"
                  >
                    Remover
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-[2] px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:shadow-xl hover:-translate-y-1 active:scale-95 disabled:opacity-50 relative overflow-hidden group/btn"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      <span>Salvando...</span>
                    </div>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Perfex CRM */}
      <PerfexSyncModal
        isOpen={activeModal === 'perfex'}
        onClose={() => setActiveModal(null)}
        perfexUrl={settings?.perfexUrl || ''}
        perfexToken={settings?.perfexToken || ''}
        onSave={async (url, token) => {
          try {
            const requestData: Record<string, unknown> = {
              perfexUrl: url,
              perfexToken: token
            };

            if (user?.role === 'SUPERADMIN') {
              const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
              if (selectedTenantId) {
                requestData.tenantId = selectedTenantId;
              }
            }

            const response = await authenticatedFetch('/api/settings', {
              method: 'PUT',
              body: JSON.stringify(requestData),
            });

            if (response.ok) {
              toast.success('Integração com Perfex CRM configurada com sucesso');
              clearSettingsCache();
              await loadSettings();
            } else {
              const errorData = await response.json();
              toast.error(errorData.error || 'Erro ao configurar Perfex CRM');
            }
          } catch (error) {
            console.error('Erro ao configurar Perfex CRM:', error);
            toast.error('Erro ao configurar Perfex CRM');
          }
        }}
      />

      {/* Modal SGA */}
      {activeModal === 'sga' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-white/10 relative z-10 animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full blur-3xl -mr-16 -mt-16" />
            
            <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-600/10 to-transparent" />
              <h3 className="relative text-2xl font-bold font-oswald text-white flex items-center gap-4 uppercase tracking-tight">
                <span className="p-2 bg-teal-500/10 rounded-xl border border-teal-500/20 text-xl shadow-[0_0_15px_rgba(20,184,166,0.3)]">🔗</span> 
                INTEGRAÇÃO SGA
              </h3>
              <button onClick={() => setActiveModal(null)} className="relative text-gray-400 hover:text-white transition-all bg-white/5 hover:bg-white/10 rounded-full w-10 h-10 flex items-center justify-center border border-white/10 hover:rotate-90 duration-300">
                <span className="text-xl">✕</span>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit, (errors) => {
              console.error('❌ Erros de validação no formulário SGA:', errors);
              sonnerToast.error('Verifique os campos obrigatórios', { style: { zIndex: 10000 } });
            })} className="relative p-8 space-y-6">
              <div className="group">
                <label htmlFor="sgaUrl" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-[0.2em] group-focus-within:text-teal-400 transition-colors ml-1">
                  URL Base SGA *
                </label>
                <input
                  id="sgaUrl"
                  type="url"
                  {...register('sgaUrl')}
                  placeholder="https://api.sga.com"
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all text-sm text-white placeholder-gray-600"
                />
                {errors.sgaUrl && (
                  <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                    {errors.sgaUrl.message}
                  </p>
                )}
              </div>

              <div className="group">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="sgaToken" className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] group-focus-within:text-teal-400 transition-colors ml-1">
                    Token SGA *
                  </label>
                  {settings?.sgaToken && (
                    <span className="text-[10px] font-bold text-teal-400 uppercase tracking-wider bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                      Configurado
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="sgaToken"
                    type="password"
                    {...register('sgaToken')}
                    placeholder={settings?.sgaToken ? "Token salvo (edite para alterar)" : "••••••••••••••••"}
                    className={`w-full px-5 py-4 bg-white/5 border rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all font-mono text-sm text-white placeholder-gray-600 ${settings?.sgaToken
                        ? 'border-teal-500/30 shadow-[0_0_15px_rgba(20,184,166,0.1)]'
                        : 'border-white/10'
                      }`}
                  />
                  {settings?.sgaToken && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-teal-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.sgaToken && (
                  <p className="text-red-400 text-[11px] mt-2 font-bold uppercase tracking-wider bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/20 inline-block animate-shake">
                    {errors.sgaToken.message}
                  </p>
                )}
              </div>

              <div className="flex gap-4 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 py-4 px-8 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm text-gray-400 bg-white/5 hover:bg-white/10 hover:text-white transition-all border border-white/10"
                >
                  Cancelar
                </button>
                {settings?.sgaUrl && settings?.sgaToken && (
                  <button
                    type="button"
                    onClick={() => removeIntegration('sga')}
                    disabled={isSubmitting}
                    className="flex-1 py-4 px-8 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all border border-red-500/20 disabled:opacity-50"
                  >
                    Remover
                  </button>
                )}
                <button
                  type="submit"
                  onClick={() => console.log('🖱️ Botão SALVAR SGA clicado')}
                  disabled={isSubmitting}
                  className="flex-1 py-4 px-8 rounded-xl font-bold font-oswald tracking-[0.2em] uppercase text-sm bg-teal-600 text-white hover:bg-teal-500 hover:shadow-xl hover:shadow-teal-900/30 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group/btn disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-1">
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/30 border-t-white" />
                      <span>Salvando...</span>
                    </div>
                  ) : (
                    'SALVAR'
                  )}
                </button>
              </div>

              {/* Area de Teste */}
              <div className="pt-6 border-t border-white/10">
                {!sgaTestedSuccessfully && (!!getValues('sgaUrl') || !!getValues('sgaToken')) && (
                  <div className="mb-4 px-4 py-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-3">
                    <span className="text-yellow-400 text-lg">⚠️</span>
                    <p className="text-[11px] text-yellow-200/80 font-medium uppercase tracking-wider">
                      Conexão não verificada. Teste para ativar a integração.
                    </p>
                  </div>
                )}
                <h4 className="text-[10px] font-bold text-gray-400 mb-4 uppercase tracking-[0.2em] ml-1">Testar Conexão</h4>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="CPF do Associado (999.999.999-99)"
                      value={testCpf}
                      onChange={(e) => setTestCpf(e.target.value)}
                      className="flex-1 px-5 py-3.5 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 text-sm text-white placeholder-gray-600"
                    />
                    <button
                      type="button"
                      onClick={handleTestSga}
                      disabled={isTestingSga || !testCpf}
                      className="px-6 py-3.5 bg-white/10 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] hover:bg-white/20 transition-all border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-xs relative overflow-hidden group/btn"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                      {isTestingSga ? 'Testando...' : 'Testar'}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-4 ml-1 font-bold uppercase tracking-[0.15em]">
                    Insira um CPF válido para verificar se a integração está respondendo corretamente.
                  </p>
                </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 backdrop-blur-3xl shadow-2xl max-w-md w-[90vw]">
          <AlertDialogHeader className="space-y-4">
            <div className={`w-16 h-16 ${confirmDialog.type === 'danger' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'} rounded-full flex items-center justify-center mx-auto mb-2 border ${confirmDialog.type === 'danger' ? 'border-red-500/20' : 'border-blue-500/20'}`}>
              {confirmDialog.type === 'danger' ? (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ) : (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-white text-center font-oswald uppercase tracking-tight">
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 text-center text-sm font-medium leading-relaxed">
              {confirmDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <AlertDialogCancel className="w-full sm:w-auto px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/10 hover:bg-white/10 hover:text-white transition-all font-oswald">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.onConfirm}
              className={`w-full sm:w-auto px-10 py-4 ${confirmDialog.type === 'danger' ? 'bg-red-600 shadow-red-900/40 border-red-500' : 'bg-blue-600 shadow-blue-900/40 border-blue-500'} text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg hover:scale-105 transition-all duration-300 border relative overflow-hidden group/btn font-oswald`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
